import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { ITreatmentPlanV3CrisisPlan } from '../../../../pages/common';
import type { TreatmentPlanV3MutationFlag, TreatmentPlanV3State } from '../../types';
import type {
  ITreatmentPlanV3CrisisPage,
  CreateTreatmentPlanV3CrisisPlanPayload,
  UpdateTreatmentPlanV3CrisisPlanPayload,
  DeleteTreatmentPlanV3CrisisPlanPayload,
  DeleteAllTreatmentPlanV3CrisisPlansPayload,
  DeleteAllTreatmentPlanV3CrisisPlansResult,
  SetTreatmentPlanV3NoCrisisPlanNeededPayload,
  TreatmentPlanV3CrisisThunks,
  CrisisMutationAction,
  CrisisMutationRequestAction,
} from './types';

export function addTreatmentPlanV3CrisisCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3CrisisThunks,
) {
  addGetTreatmentPlanV3CrisisPageCases(builder, thunks);
  addCreateTreatmentPlanV3CrisisPlanCases(builder, thunks);
  addUpdateTreatmentPlanV3CrisisPlanCases(builder, thunks);
  addDeleteTreatmentPlanV3CrisisPlanCases(builder, thunks);
  addDeleteAllTreatmentPlanV3CrisisPlansCases(builder, thunks);
  addSetTreatmentPlanV3NoCrisisPlanNeededCases(builder, thunks);
}

function startCrisisMutation(
  state: TreatmentPlanV3State,
  flag: TreatmentPlanV3MutationFlag,
  action: CrisisMutationRequestAction,
) {
  startTreatmentPlanV3Mutation(state, flag, action);
  if (state.crisisPage != null && state.crisisPageRequestId) {
    state.crisisMutationLoadRequestIds[action.meta.requestId] = state.crisisPageRequestId;
  }
}

function finishCrisisMutation(
  state: TreatmentPlanV3State,
  flag: TreatmentPlanV3MutationFlag,
  action: CrisisMutationRequestAction,
) {
  const loadRequestId = state.crisisMutationLoadRequestIds[action.meta.requestId];
  delete state.crisisMutationLoadRequestIds[action.meta.requestId];
  return (
    finishTreatmentPlanV3Mutation(state, flag, action) &&
    loadRequestId === state.crisisPageRequestId &&
    action.meta.arg.treatmentPlanId === state.crisisPageTreatmentPlanId
  );
}

function addGetTreatmentPlanV3CrisisPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3CrisisThunks,
) {
  const { getTreatmentPlanV3CrisisPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3CrisisPage.pending, (state, action) => {
      state.crisisPage = null;
      state.crisisPageLoading = true;
      state.crisisPageError = null;
      state.crisisPageRequestId = action.meta.requestId;
      state.crisisPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3CrisisPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.crisisPageRequestId) return;
      state.crisisPageLoading = false;
      state.crisisPageError = null;
      state.crisisPage = action.payload;
    })
    .addCase(getTreatmentPlanV3CrisisPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.crisisPageRequestId) return;
      state.crisisPageLoading = false;
      state.crisisPageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateTreatmentPlanV3CrisisPlanCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3CrisisThunks,
) {
  const { createTreatmentPlanV3CrisisPlan } = thunks;
  const flag = 'createCrisisPlanLoading';

  builder
    .addCase(createTreatmentPlanV3CrisisPlan.pending, (state, action) => {
      startCrisisMutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3CrisisPlan.fulfilled,
      (
        state,
        action: CrisisMutationAction<
          ITreatmentPlanV3CrisisPlan,
          CreateTreatmentPlanV3CrisisPlanPayload
        >,
      ) => {
        if (!finishCrisisMutation(state, flag, action)) return;
        state.crisisPage?.crisis_plans.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3CrisisPlan.rejected, (state, action) => {
      if (!finishCrisisMutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addUpdateTreatmentPlanV3CrisisPlanCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3CrisisThunks,
) {
  const { updateTreatmentPlanV3CrisisPlan } = thunks;
  const flag = 'updateCrisisPlanLoading';

  builder
    .addCase(updateTreatmentPlanV3CrisisPlan.pending, (state, action) => {
      startCrisisMutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3CrisisPlan.fulfilled,
      (
        state,
        action: CrisisMutationAction<
          ITreatmentPlanV3CrisisPlan,
          UpdateTreatmentPlanV3CrisisPlanPayload
        >,
      ) => {
        if (!finishCrisisMutation(state, flag, action)) return;
        const list = state.crisisPage?.crisis_plans;
        if (!list) return;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3CrisisPlan.rejected, (state, action) => {
      if (!finishCrisisMutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3CrisisPlanCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3CrisisThunks,
) {
  const { deleteTreatmentPlanV3CrisisPlan } = thunks;
  const flag = 'deleteCrisisPlanLoading';

  builder
    .addCase(deleteTreatmentPlanV3CrisisPlan.pending, (state, action) => {
      startCrisisMutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3CrisisPlan.fulfilled,
      (state, action: CrisisMutationAction<void, DeleteTreatmentPlanV3CrisisPlanPayload>) => {
        if (!finishCrisisMutation(state, flag, action) || !state.crisisPage) return;
        state.crisisPage.crisis_plans = state.crisisPage.crisis_plans.filter(
          (row) => row.id !== action.meta.arg.crisisPlanId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3CrisisPlan.rejected, (state, action) => {
      if (!finishCrisisMutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function removeCrisisPlansById(state: TreatmentPlanV3State, deletedIds: number[]) {
  if (!state.crisisPage) return;
  state.crisisPage.crisis_plans = state.crisisPage.crisis_plans.filter(
    (row) => !deletedIds.includes(row.id),
  );
}

function addDeleteAllTreatmentPlanV3CrisisPlansCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3CrisisThunks,
) {
  const { deleteAllTreatmentPlanV3CrisisPlans } = thunks;
  const flag = 'deleteAllCrisisPlansLoading';

  builder
    .addCase(deleteAllTreatmentPlanV3CrisisPlans.pending, (state, action) => {
      startCrisisMutation(state, flag, action);
    })
    .addCase(
      deleteAllTreatmentPlanV3CrisisPlans.fulfilled,
      (
        state,
        action: CrisisMutationAction<
          DeleteAllTreatmentPlanV3CrisisPlansResult,
          DeleteAllTreatmentPlanV3CrisisPlansPayload
        >,
      ) => {
        if (!finishCrisisMutation(state, flag, action)) return;
        removeCrisisPlansById(state, action.payload.deletedIds);
      },
    )
    .addCase(deleteAllTreatmentPlanV3CrisisPlans.rejected, (state, action) => {
      if (!finishCrisisMutation(state, flag, action)) return;
      if (action.payload) {
        removeCrisisPlansById(state, action.payload.deletedIds);
      }
      toast.error(action.payload?.message ?? getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addSetTreatmentPlanV3NoCrisisPlanNeededCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3CrisisThunks,
) {
  const { setTreatmentPlanV3NoCrisisPlanNeeded } = thunks;
  const flag = 'setNoCrisisPlanNeededLoading';

  builder
    .addCase(setTreatmentPlanV3NoCrisisPlanNeeded.pending, (state, action) => {
      startCrisisMutation(state, flag, action);
    })
    .addCase(
      setTreatmentPlanV3NoCrisisPlanNeeded.fulfilled,
      (
        state,
        action: CrisisMutationAction<
          ITreatmentPlanV3CrisisPage,
          SetTreatmentPlanV3NoCrisisPlanNeededPayload
        >,
      ) => {
        if (!finishCrisisMutation(state, flag, action)) return;
        state.crisisPage = action.payload;
      },
    )
    .addCase(setTreatmentPlanV3NoCrisisPlanNeeded.rejected, (state, action) => {
      if (!finishCrisisMutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
